import { vestingPillHref } from './vesting-pill';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { generatePath } from 'react-router-dom';
import {
  Button,
  Tag,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import type { DataTableColumnDef } from 'components/data-table';
import { blanksLast } from 'components/data-table/utils';
import { Link, PlainLink } from 'components/link';
import routes from '~/routes';
import type { VestingDocumentIndicator } from 'types/api-responses';
import {
  EMPTY,
  vestingDocumentLabel,
} from 'components/vesting-documents/types';
import {
  citationColumns,
  col,
  listCol,
} from 'components/vesting-documents/citation-columns';
import {
  VESTING_STATE_TAGS,
  type VestingState,
} from 'components/vesting-documents/vesting-document-card';
import { readWriteFailure } from 'pages/well-workspaces/utils';
import type {
  DedupFields,
  MatchStatus,
  Verdict,
  OwnershipRow,
  ReportAgreement,
  RunsheetRow,
  WorkspaceTract,
  WorkspaceVestingRow,
} from '../types';
import { TARGET_NPV_META, targetNpvSumColumn } from 'components/target-npv';
import { LinkList } from '../party-links';
import styles from './page.module.scss';

export const MATCH_LABELS: Record<MatchStatus, string> = {
  matched: 'Matched',
  manual: 'Manual',
  ambiguous: 'Ambiguous',
  absent: 'Absent',
  unmatched: 'Unmatched',
};
export const VERDICT_LABELS: Record<Verdict, string> = {
  match: 'Match',
  value_mismatch: 'Value mismatch',
  missing_in_counterparty: 'Missing in CRM',
  not_comparable: 'Not comparable',
  entity_unlinked: 'Entity unlinked',
  unresolved_tract: 'Unresolved tract',
};

export const REPORT_AGREEMENT_LABELS: Record<ReportAgreement, string> = {
  agree: 'Agree',
  disagree: 'Disagree',
  single: 'Single report',
};

export const CARDS = [
  { id: 'tracts', label: 'Tracts' },
  { id: 'title-support', label: 'Title Support' },
  { id: 'standard-mor', label: 'Ownership' },
  { id: 'runsheet-comparison', label: 'Runsheet Comparison' },
  { id: 'ownership-comparison', label: 'Ownership Comparison' },
] as const;
export type CardId = (typeof CARDS)[number]['id'];
export type CardVisibility = Record<string, boolean | undefined>;
export type JumpToCard = (card: CardId) => void;
export const NO_ROWS: never[] = [];

export const dateCol = <Row extends Record<string, unknown>>(
  id: string,
  header: string,
  size: number
) => col<Row>(id, header, size, { type: 'date' });

const decimal8Col = <Row extends Record<string, unknown>>(
  id: string,
  header: string,
  size: number
) =>
  col<Row>(id, header, size, {
    type: 'decimal',
    format: { maxDecimalPlaces: 8 },
  });

export const tractHref = (salesforceTractId: string) =>
  generatePath(routes.crmTractWizardEditPath, { tractId: salesforceTractId });

export const linkOr = (
  text: string | null | undefined,
  href: string | null | undefined,
  title?: string | null
) => {
  if (!text) return EMPTY;
  if (!href) return text;
  return (
    <Link to={href} title={title ?? undefined}>
      {text}
    </Link>
  );
};

// A pinned Actions column clips a top tooltip; left stays inside it.
export const RowAction = (props: ComponentProps<typeof Button>) => (
  <Button
    kind="ghost"
    size="sm"
    hasIconOnly
    tooltipPosition="left"
    tooltipAlignment="center"
    {...props}
  />
);

/**
 * The props a confirmation modal takes from the write it confirms: it can't be submitted
 * or closed while the write runs, and it shows the write's failure.
 *
 * @param {object} write - The write's mutation result.
 * @param {boolean} write.isPending - The write is running.
 * @param {unknown} write.error - The write's failure, if any.
 * @param {string} failure - The message for a failure the server didn't explain.
 * @param {Function} close - Closes the modal.
 * @return {object} The modal props.
 */
export const confirmProps = (
  write: { isPending: boolean; error: unknown },
  failure: string,
  close: () => void
) => ({
  primaryButtonDisabled: write.isPending,
  preventCloseOnClickOutside: write.isPending,
  error: readWriteFailure(write.error, failure) ?? undefined,
  onRequestClose: () => {
    if (!write.isPending) close();
  },
});

export const sourceFilenames = (row: DedupFields) =>
  row.sourceSupports.map((support) => support.filename);

export const sourceSheetNames = (row: DedupFields) => [
  ...new Set(row.sourceSupports.flatMap((support) => support.sheetNames)),
];

// Keeps a row carrying any one selected value.
export const anySelected = (values: string[], selected: string[]) =>
  !selected?.length || selected.some((value) => values.includes(value));

const sourceCol = <Row extends Record<string, unknown> & DedupFields>(
  id: string,
  header: string,
  size: number,
  values: (row: DedupFields) => string[],
  render: (joined: string) => ReactNode = (joined) => joined
): DataTableColumnDef<Row> => ({
  id,
  accessorFn: (row) => values(row).join(', '),
  header,
  size,
  // Derived from the row's workbooks; the paged tables' server cannot sort it.
  enableSorting: false,
  meta: { type: 'enum', wrap: true },
  filterFn: (row, _columnId, selected: string[]) =>
    anySelected(values(row.original), selected),
  cell: ({ row }) => {
    const joined = values(row.original).join(', ');
    return joined ? render(joined) : EMPTY;
  },
});

export const workbookCols = <Row extends Record<string, unknown> & DedupFields>(
  onJump?: JumpToCard
): DataTableColumnDef<Row>[] => [
  sourceCol<Row>(
    'workbook',
    'Workbook',
    220,
    sourceFilenames,
    onJump &&
      ((names) => (
        <PlainLink
          href="#title-support"
          title={names}
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            onJump('title-support');
          }}
        >
          {names}
        </PlainLink>
      ))
  ),
  sourceCol<Row>('sheetName', 'Sheet', 160, sourceSheetNames),
];

// The CSV carries the Not in workspace tag, so its rows reconcile with the stats bar.
export const notInWorkspace = (text: string, inWorkspace?: boolean) => {
  if (inWorkspace === false) return `${text} (Not in workspace)`;
  return text;
};

export const RUNSHEET_COLUMNS: DataTableColumnDef<RunsheetRow>[] = [
  ...workbookCols<RunsheetRow>(),
  ...citationColumns<RunsheetRow>(),
  col('notes', 'Notes', 300, { type: 'text', wrap: true }),
];

export const MOR_COLUMNS: DataTableColumnDef<OwnershipRow>[] = [
  {
    id: 'sources',
    accessorFn: (row) => row.sourceSupports.length,
    header: 'Sources',
    size: 90,
    enableSorting: false,
    cell: ({ row }) => (
      <span title={sourceFilenames(row.original).join(', ')}>
        {row.original.sourceSupports.length}
      </span>
    ),
  },
  {
    ...col<OwnershipRow>('reportAgreement', 'Report Agreement', 170, {
      type: 'enum',
      overflowVisible: true,
      getExportValue: (row) =>
        REPORT_AGREEMENT_LABELS[
          (row as OwnershipRow).reportAgreement ?? 'single'
        ],
    }),
    cell: ({ row }) => {
      const { reportAgreement = 'single', agreementDetail = [] } = row.original;
      const label = REPORT_AGREEMENT_LABELS[reportAgreement];
      if (reportAgreement === 'disagree')
        return (
          <>
            <Tag type="magenta" size="sm">
              {label}
            </Tag>
            <Toggletip align="bottom-start" autoAlign>
              <ToggletipButton label="What each report states">
                <Information />
              </ToggletipButton>
              <ToggletipContent>
                {agreementDetail.map((detail) => (
                  <p key={detail.filename}>
                    {`${detail.filename}: ${detail.values}`}
                  </p>
                ))}
              </ToggletipContent>
            </Toggletip>
          </>
        );
      return (
        <Tag type={reportAgreement === 'agree' ? 'green' : 'gray'} size="sm">
          {label}
        </Tag>
      );
    },
  },
  ...workbookCols<OwnershipRow>(),
  col('entityName', 'Entity', 320, { type: 'enum' }),
  {
    ...col<OwnershipRow>('tractIdentifier', 'Tract', 260, {
      type: 'enum',
      getExportValue: (row) => {
        const { tractIdentifier, tractInWorkspace } = row as OwnershipRow;
        return notInWorkspace(tractIdentifier ?? EMPTY, tractInWorkspace);
      },
    }),
    cell: ({ row }) => {
      const { tractIdentifier, salesforceTractId, tractInWorkspace } =
        row.original;
      return (
        <span className={styles.pills}>
          {linkOr(
            tractIdentifier,
            salesforceTractId && tractHref(salesforceTractId)
          )}
          {tractInWorkspace === false && (
            <Tag type="gray" size="sm">
              Not in workspace
            </Tag>
          )}
        </span>
      );
    },
  },
  col('legalDescription', 'Legal Description', 300, {
    type: 'text',
    wrap: true,
  }),
  listCol('quarterCalls', 'Quarter Calls', 180),
  col('surveyLocation', 'Survey', 140),
  col('countyName', 'County', 120),
  col('stateName', 'State', 80),
  decimal8Col('grossAcres', 'Gross Acres', 130),
  col('depthStartFt', 'Depth Start', 130, { type: 'integer' }),
  col('depthEndFt', 'Depth End', 120, { type: 'integer' }),
  col('depthNote', 'Depth Note', 130, { type: 'text', wrap: true }),
  col('leaseReference', 'Lease', 130),
  // lease_book holds the volume ("312" of "312/847"); the runsheet already calls it Volume.
  col('leaseBook', 'Lease Volume', 130),
  col('leasePage', 'Lease Page', 130),
  decimal8Col('leaseRoyaltyRate', 'Lease Royalty', 150),
  decimal8Col('executive', 'Executive', 130),
  decimal8Col('bonus', 'Bonus', 120),
  decimal8Col('delayRental', 'Delay Rental', 140),
  decimal8Col('royalty', 'Royalty', 120),
  decimal8Col('workingInterest', 'Working Interest', 170),
  decimal8Col('surface', 'Surface', 120),
  decimal8Col('orri', 'ORRI', 120),
  decimal8Col('nri', 'NRI', 120),
  decimal8Col('netMineralAcres', 'Net Mineral Acres', 180),
  decimal8Col('netRoyaltyAcres', 'Net Royalty Acres', 180),
];

export type TractRow = WorkspaceTract &
  Record<string, unknown> & {
    id: string;
    entities: { id: number; name: string }[];
    vestingDocument: VestingDocumentIndicator | null;
  };

export const PENDING_REMOVAL = 'Pending removal';

const vestingRow = (tract: WorkspaceTract, key: string | null) =>
  tract.vestingDocuments.find((row) => row.key === key) ?? null;

const preferredVesting = (tract: WorkspaceTract) =>
  vestingRow(tract, tract.preferredVestingKey);

export const shownVesting = (tract: WorkspaceTract) =>
  vestingRow(tract, tract.shownVestingKey);

type SharedVestingRow = Extract<
  WorkspaceVestingRow,
  { pending: 'remove' | null }
>;

export const awaitingVerify = (tract: WorkspaceTract) =>
  tract.vestingDocuments.find(
    (row): row is SharedVestingRow =>
      row.pending !== 'add' && row.id === tract.awaitingVerifyId
  ) ?? null;

// Not the shared VestingStatusTag, which reads Needs Verification and extraction states.
const VestingTag = ({ state }: { state: VestingState }) => {
  const [type, label] = VESTING_STATE_TAGS[state];
  return <Tag type={type}>{label}</Tag>;
};

export const vestingPillLabel = (
  indicator: VestingDocumentIndicator | null,
  state: WorkspaceTract['vestingState']
) => (!indicator || state === 'none' ? EMPTY : VESTING_STATE_TAGS[state][1]);

export const VestingPill = ({
  indicator,
  state,
}: {
  indicator: VestingDocumentIndicator | null;
  state: WorkspaceTract['vestingState'];
}) => {
  const href = vestingPillHref(indicator, state);
  // The state check narrows the type; vestingPillHref already returns null for it.
  if (!href || state === 'none') return <>{EMPTY}</>;
  return (
    <Link to={href} openInNewTab>
      <VestingTag state={state} />
    </Link>
  );
};

export const actionsCol = <Row extends Record<string, unknown>>(
  size: number,
  cell: DataTableColumnDef<Row>['cell']
): DataTableColumnDef<Row> => ({
  id: 'actions',
  header: 'Actions',
  size,
  enableSorting: false,
  meta: { overflowVisible: true, isExportable: false },
  cell,
});
export const PIN_ACTIONS = { columnPinning: { left: [], right: ['actions'] } };

export interface DocumentRow extends Record<string, unknown> {
  key: string;
  label: string;
  href: string | null;
  verified: boolean | null;
  vestingState: VestingState | null;
  tractCount: number;
  tractNames: string;
  netAcres: number | null;
  /** The tracts' shared unit; null when they differ, and then netAcres is not summed. */
  netAcreageUnits: string | null;
  grossAcres: number | null;
  targetNpvIncluded: number | null;
  targetNpvValuedCount: number;
  tracts: TractRow[];
}

const sumPresent = (values: (number | null)[]) => {
  const present = values.filter((v): v is number => typeof v === 'number');
  return present.length ? present.reduce((a, b) => a + b, 0) : null;
};

// Tracts without a vesting document share one row.
export const groupByDocument = (rows: TractRow[]): DocumentRow[] => {
  const groups = new Map<string, TractRow[]>();
  rows.forEach((row) => {
    const key = String(preferredVesting(row)?.document.id ?? '');
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });
  return [...groups].map(([key, tracts]) => {
    const vesting = preferredVesting(tracts[0]);
    const npv = tracts.map((row) => row.targetNpvIncluded);
    const units = [...new Set(tracts.map((row) => row.netAcreageUnits))];
    return {
      key: key || 'none',
      label: vesting ? vestingDocumentLabel(vesting) : 'No vesting document',
      href: vesting?.document.href ?? null,
      verified: vesting ? vesting.verified : null,
      // Pending only while every tract's shown row waits on promote.
      vestingState:
        vesting &&
        (vesting.verified || tracts.every((row) => row.vestingStaged)
          ? vesting.vestingState
          : 'unverified'),
      tractCount: tracts.length,
      tractNames: tracts.map((row) => row.name).join(', '),
      // NMA and NRA do not add up; a mixed group has no one total.
      netAcres:
        units.length === 1
          ? sumPresent(tracts.map((row) => row.netAcres))
          : null,
      netAcreageUnits: units.length === 1 ? units[0] : null,
      grossAcres: sumPresent(tracts.map((row) => row.grossAcres)),
      // Partial, as on the workspace list: the tracts that have a value.
      targetNpvIncluded: sumPresent(npv),
      targetNpvValuedCount: npv.filter((v) => typeof v === 'number').length,
      tracts,
    };
  });
};

export const DOCUMENT_COLUMNS: DataTableColumnDef<DocumentRow>[] = [
  {
    ...col<DocumentRow>('label', 'Vesting Document', 320, {
      type: 'text',
      getExportHref: (row) => (row as DocumentRow).href,
    }),
    cell: ({ row }) => linkOr(row.original.label, row.original.href),
  },
  {
    id: 'verified',
    accessorKey: 'verified',
    header: 'Verified',
    size: 180,
    meta: {
      getExportValue: (row) => {
        const { vestingState } = row as DocumentRow;
        return vestingState ? VESTING_STATE_TAGS[vestingState][1] : EMPTY;
      },
    },
    cell: ({ row }) => {
      const { vestingState } = row.original;
      return vestingState ? <VestingTag state={vestingState} /> : EMPTY;
    },
  },
  col('tractCount', 'Tracts', 100, { type: 'integer' }),
  {
    ...col<DocumentRow>('tractNames', 'Tract Names', 260),
    cell: ({ row }) => (
      <LinkList
        links={row.original.tracts.map((tract) => ({
          key: tract.id,
          to: tractHref(tract.id),
          label: tract.name,
        }))}
      />
    ),
  },
  decimal8Col('netAcres', 'Net Acres', 120),
  col<DocumentRow>('netAcreageUnits', 'Net Acreage Units', 150),
  decimal8Col('grossAcres', 'Gross Acres', 130),
  {
    ...blanksLast<DocumentRow>('targetNpvIncluded'),
    size: 190,
    ...targetNpvSumColumn<DocumentRow>(
      (row) => [
        row.targetNpvIncluded,
        row.targetNpvValuedCount,
        row.tractCount,
      ],
      TARGET_NPV_META
    ),
  },
];
